import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { BookingLinkDto, BookingSettingsDto, toBookingLink, toBookingSettings, toBookingSettingsDto } from '../models/calendar-api.models';
import { AgentBookingSettings, BookingLink, DayWindows, Weekday } from '../models/calendar.models';
import { CalendarHttpService } from './calendar-http.service';

/** Default working window applied to enabled weekdays when no settings exist yet. */
const DEFAULT_WINDOW: [number, number] = [9, 17];

/** Mon–Fri are bookable by default; weekends off. */
function defaultSchedule(): DayWindows[] {
  return ([0, 1, 2, 3, 4, 5, 6] as Weekday[]).map(weekday => ({
    weekday,
    enabled: weekday >= 1 && weekday <= 5,
    windows: [[...DEFAULT_WINDOW] as [number, number]],
  }));
}

/**
 * Placeholder used while the real record is in flight, and when an agent has none.
 *
 * Flagged `isDefault` for the same reason the backend flags its own default response: a
 * populated 9–17 grid must not read as "saved".
 */
export function defaultBookingSettings(projectId: string, agentId: string): AgentBookingSettings {
  return {
    projectId,
    agentId,
    publicBookingEnabled: false,
    slotDurationMin: 30,
    bufferMin: 0,
    schedule: defaultSchedule(),
    bookingSlug: agentId,
    isDefault: true,
  };
}

/**
 * Booking behaviour per (project, agent), served from `/api/v1/calendar/booking-settings`.
 *
 * Previously an in-memory `Map` mirrored into localStorage, which is why working hours
 * "wouldn't update": they were per-browser, invisible to a second device, and invisible to the
 * public booking page that has to compute slots from them.
 *
 * The day/week grids need the schedule synchronously while rendering, so reads are also kept in
 * a local cache: {@link snapshot} answers immediately from it and lazily fetches anything
 * missing, then emits on {@link changed$} so the open grid re-trims once the real record lands.
 */
@Injectable({ providedIn: 'root' })
export class CalendarSettingsService {
  private readonly http = inject(CalendarHttpService);

  private static readonly PATH = '/booking-settings';

  private readonly cache = new Map<string, AgentBookingSettings>();
  /** Keys with a fetch in flight, so a re-render doesn't queue the same request repeatedly. */
  private readonly pending = new Set<string>();
  private readonly changedSubject = new BehaviorSubject<string>('');

  /** Emits the `project:agent` key whenever settings are saved or first loaded. */
  readonly changed$ = this.changedSubject.asObservable();

  /**
   * Current settings for the pair. `agentId` defaults to the logged-in user server-side.
   *
   * When nothing is saved yet the backend returns the 9–17 Mon–Fri default with
   * `is_default: true` — render it, but treat it as unsaved.
   */
  get(projectId: string | null, agentId?: string): Observable<AgentBookingSettings> {
    const key = this.key(projectId ?? '', agentId ?? '');
    return this.http.get<BookingSettingsDto>(CalendarSettingsService.PATH, { project_id: projectId, agent_id: agentId }).pipe(
      map(dto => toBookingSettings(dto ?? ({} as BookingSettingsDto), projectId ?? '', agentId ?? '')),
      tap(settings => this.cache.set(this.key(settings.projectId, settings.agentId) || key, settings))
    );
  }

  save(settings: AgentBookingSettings): Observable<AgentBookingSettings> {
    return this.http.put<BookingSettingsDto>(CalendarSettingsService.PATH, toBookingSettingsDto(settings)).pipe(
      map(dto => toBookingSettings(dto ?? ({} as BookingSettingsDto), settings.projectId, settings.agentId)),
      tap(saved => {
        this.cache.set(this.key(saved.projectId, saved.agentId), saved);
        this.changedSubject.next(this.key(saved.projectId, saved.agentId));
      })
    );
  }

  /**
   * Signed public booking link for this calendar. The `t` token can only be minted server-side,
   * so a hand-assembled `/book/...` URL 404s for the recipient.
   *
   * Fails with 400 `"Public booking is not enabled for this calendar yet"` until the agent turns
   * the link on in Booking settings.
   */
  getBookingLink(projectId: string | null, agentId?: string, typeId?: string): Observable<BookingLink> {
    return this.http.get<BookingLinkDto>('/booking-link', { project_id: projectId, agent_id: agentId, type_id: typeId }).pipe(map(toBookingLink));
  }

  /**
   * Synchronous read for the grid. Returns the cached record, or the default while the real one
   * loads in the background (see {@link changed$}).
   */
  snapshot(projectId: string, agentId: string): AgentBookingSettings {
    const cached = this.cache.get(this.key(projectId, agentId));
    if (cached) return cached;
    this.ensureLoaded(projectId, agentId);
    return defaultBookingSettings(projectId, agentId);
  }

  /**
   * The combined working window and enabled weekdays across the given agents — used by the
   * day/week grid to only render working hours and hide non-working days. Falls back to the
   * default 9–17, Mon–Fri when no agent is passed.
   */
  visibleWindowForAgents(projectId: string, agentIds: string[]): { startHour: number; endHour: number; weekdays: Weekday[] } {
    const ids = agentIds.length ? agentIds : [''];
    let startHour = 24;
    let endHour = 0;
    const weekdays = new Set<Weekday>();
    for (const id of ids) {
      for (const day of this.snapshot(projectId, id).schedule) {
        if (!day.enabled) continue;
        weekdays.add(day.weekday);
        for (const [ws, we] of day.windows) {
          if (ws < startHour) startHour = ws;
          if (we > endHour) endHour = we;
        }
      }
    }
    if (startHour >= endHour) {
      startHour = 9;
      endHour = 17;
    }
    if (!weekdays.size) {
      [1, 2, 3, 4, 5].forEach(d => weekdays.add(d as Weekday));
    }
    return { startHour, endHour, weekdays: [...weekdays] };
  }

  /**
   * Fetch a pair once and announce it. A failure caches the default so a grid that re-renders on
   * every tick can't turn one unreachable agent into an endless request loop.
   */
  private ensureLoaded(projectId: string, agentId: string): void {
    const key = this.key(projectId, agentId);
    if (!agentId || this.cache.has(key) || this.pending.has(key)) return;
    this.pending.add(key);
    this.get(projectId, agentId).subscribe({
      next: () => {
        this.pending.delete(key);
        this.changedSubject.next(key);
      },
      error: () => {
        this.pending.delete(key);
        this.cache.set(key, defaultBookingSettings(projectId, agentId));
      },
    });
  }

  private key(projectId: string, agentId: string): string {
    return `${projectId}:${agentId}`;
  }
}
